<template>
  <wrapper-page ref="wrapper" @search="localSearch">
    <template v-slot:MainContentHeaderActions>
      <div class="col-md-6 col-sm-12 text-end">
        <div class="input-group">
          <router-link
            :to="{ name: 'vessels-create' }"
            class="btn btn-sm btn-info"
            style="margin-left: auto"
          >
            New Vessel
          </router-link>
        </div>
      </div>
    </template>

    <template v-slot:search>
      <div class="d-flex align-items-center gap-x-2 position-relative">
        <SearchInput
          @search="localSearch"
          :results="searchResult?.results ?? []"
        />
        <div class="dropdown d-inline-block">
          <button
            class="btn header-item noti-icon waves-effect"
            data-bs-toggle="dropdown"
            type="button"
          >
            <i class="ri-settings-4-line"></i>
          </button>
          <div class="dropdown-menu dropdown-menu-lg dropdown-menu-end p-4">
            <div class="d-flex flex-column gap-y-2">
              <div
                v-for="field in searchProperties"
                :key="field.value"
                class="w-100 d-flex gap-x-2 align-items-center"
              >
                <input
                  type="checkbox"
                  v-model="searchFields[field.value]"
                  :name="`search-property-${field.value}`"
                />
                <label :for="`search-property-${field.value}`" class="mb-0">
                  {{ field.text }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="row">
      <div class="col-12 d-flex justify-content-end mb-3">
        <button
          class="btn btn-lg p-0 me-2"
          title="Show as Grid"
          @click="showAsGrid = true"
        >
          <i class="ri-layout-grid-line"></i>
        </button>
        <button
          class="btn btn-lg p-0"
          title="Show as Table"
          @click="showAsGrid = false"
        >
          <i class="ri-list-check"></i>
        </button>
      </div>
    </div>
    <template v-if="!isLoadingVessels && sortedVessels">
      <vessels-grid v-if="showAsGrid" :vessels="sortedVessels" />
      <vessels-table
        v-else
        :showSearchBar="false"
        :vessels="sortedVessels"
        :mode="currentTableMode"
      />
    </template>
  </wrapper-page>
</template>

<script>
import WrapperPage from "@/components/layout/WrapperPage.vue";
import { getVesselsList, getStakeholdersList } from "@/apis/vessels";
import VesselsGrid from "@/components/vessels/VesselsGrid";
import VesselsTable from "@/components/vessels/VesselsTable";
import { notifyError } from "@/helpers/notification";
import SearchInput from "@/components/SearchInput.vue";

const VesselSearchableProperties = [
  { text: "Name", value: "name" },
  { text: "Official Number", value: "officialNumber" },
  { text: "S Reg", value: "sReg" },
  { text: "Fisheries Id", value: "fisheriesId" },
  { text: "IMO Number", value: "imoNumber" },
  { text: "Call Sign", value: "callSign" },
  { text: "MMSI Number", value: "mmsiNumber" },
  { text: "Registry", value: "registry" },
  { text: "Flag", value: "flag" },
  { text: "Former Registry", value: "formerRegistry" },
  { text: "Class", value: "class" },
  { text: "Class Notation", value: "classNotation" },
  { text: "Operational Range", value: "operationalRange" },
  { text: "Radio Range", value: "radioRange" },
  { text: "Operational Restrictions", value: "operationalRestrictions" },
];

export default {
  name: "VesselsDashboardLists",
  components: {
    SearchInput,
    VesselsTable,
    VesselsGrid,
    WrapperPage,
  },
  data() {
    return {
      isLoadingVessels: true,
      vessels: [],
      showAsGrid: false,
      searchString: "",
      searchFields: { name: true },
      searchProperties: VesselSearchableProperties,
      searchResult: { results: [] },
    };
  },
  computed: {
    displayedVessels() {
      if (!this.searchString || !this.searchString.trim()) {
        return this.vessels;
      }

      const query = this.searchString.toLowerCase().trim();
      const activeFields = Object.keys(this.searchFields).filter(
        (key) => this.searchFields[key]
      );

      if (activeFields.length === 0) {
        return this.vessels;
      }

      return this.vessels.filter((vessel) => {
        return activeFields.some((field) => {
          const value = vessel[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    },

    currentTableMode() {
      const filter = this.$route.query.mode;
      if (!filter) return "standard";
      if (filter.includes("commercial")) return "commercial";
      if (filter.includes("ncv")) return "ncv";
      if (filter.includes("cvc")) return "cvc";
      return "standard";
    },

    sortedVessels() {
      return [...this.displayedVessels].sort((v1, v2) =>
        v1.last_updated < v2.last_updated
          ? 1
          : v1.last_updated > v2.last_updated
          ? -1
          : 0
      );
    },
  },
  methods: {
    async fetchCVCStakeholders(cvcVessels) {
      if (!cvcVessels?.length) return;

      const vesselIds = cvcVessels.map((v) => v.id);
      if (!vesselIds.length) return;

      const stakeholders = await getStakeholdersList(vesselIds);

      this.vessels = cvcVessels.map((vessel) => {
        const match = stakeholders?.data?.filter(
          (s) => s.vessel_id === vessel.id
        );
        return match.length ? { ...vessel, stakeholders: match } : vessel;
      });
    },

    getFakeIconForSearchResult(vessel) {
      return vessel.name
        .split(" ")
        .map((part) => (part[0] || "").toString().toUpperCase())
        .slice(0, 2)
        .join("");
    },

    getSubtitleForSearchResult(vessel) {
      for (let i = 0; i < this.searchProperties.length; i++) {
        const field = this.searchProperties[i];
        if (field.value === "name") continue;
        if (this.searchFields[field.value] && vessel[field.value]) {
          return field.text + " : " + vessel[field.value];
        }
      }
      return "";
    },

    async localSearch(searchString) {
      const activeFieldsCount = Object.keys(this.searchFields).filter(
        (key) => this.searchFields[key]
      ).length;

      if (activeFieldsCount === 0) {
        notifyError(this, "Choose 1 or more properties to search in");
        return;
      }

      this.searchString = searchString;
    },

    async loadVessels() {
      this.isLoadingVessels = true;
      const mode = this.$route.query.mode;

      try {
        const { data } = await getVesselsList({ mode });
        this.vessels = [...data];
        if (this.currentTableMode === "cvc") {
          await this.fetchCVCStakeholders(this.vessels);
        }
      } catch (err) {
        notifyError(this, err?.response?.data);
      } finally {
        this.isLoadingVessels = false;
      }
    },
  },
  async mounted() {
    this.loadVessels();
  },
  watch: {
    "$route.query"() {
      this.loadVessels();
    },
  },
};
</script>
